import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Article } from "@/data/articles";
import {
  FALLBACK_IMAGE,
  DEFAULT_CATEGORY_SLUG,
  DEFAULT_READING_TIME,
  formatArticleDate,
  titleCaseSlug,
} from "@/lib/article";

/** Articles with optional tags surfaced for related-by-tags. */
export type ArticleWithTags = Article & { tags?: string[] };

/**
 * Fetches all published articles from the database.
 */
export function useArticlesData() {
  const {
    data: articles = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["articles", "published"],
    queryFn: async () => {
      const cols =
        "id, title, slug, excerpt, featured_image, reading_time, author_name, published_at, created_at, updated_at, category_id, tags, categories(slug, name, color, image, icon)";

      // Prefer ranking data (view_count) for the "Most Read" section, but fall
      // back gracefully when the column hasn't been migrated into this
      // environment yet — selecting a missing column hard-fails (500), which is
      // what previously forced view_count out of this query. The fallback keeps
      // the homepage alive and the ranking self-heals once the migration lands.
      const withViews = await supabase
        .from("articles")
        .select(`${cols}, view_count`)
        .eq("status", "published")
        .order("published_at", { ascending: false, nullsFirst: false });

      const { data, error: dbError } =
        withViews.error && /view_count/i.test(withViews.error.message)
          ? await supabase
              .from("articles")
              .select(cols)
              .eq("status", "published")
              .order("published_at", { ascending: false, nullsFirst: false })
          : withViews;

      if (dbError) throw dbError;

      return (data || []).map((row) => {
        const cats = row.categories as {
          slug: string;
          name: string;
          color: string | null;
          image: string | null;
          icon: string | null;
        } | null;
        const catSlug = cats?.slug || DEFAULT_CATEGORY_SLUG;
        return {
          id: row.id,
          title: row.title,
          slug: row.slug,
          excerpt: row.excerpt || "",
          image: row.featured_image || FALLBACK_IMAGE,
          category: catSlug,
          categoryLabel: cats?.name || titleCaseSlug(catSlug),
          categoryColor: cats?.color ?? null,
          categoryImage: cats?.image ?? null,
          categoryIcon: cats?.icon ?? null,
          date: formatArticleDate(row.published_at || row.created_at),
          publishedAt: row.published_at || row.created_at,
          updatedAt: row.updated_at,
          readingTime: row.reading_time || DEFAULT_READING_TIME,
          authorName: row.author_name,
          tags: row.tags || [],
          viewCount: (row as { view_count?: number }).view_count ?? 0,
        } satisfies Article;
      });
    },
  });

  const getByCategory = useMemo(
    () => (cat: string) => articles.filter((a) => a.category === cat),
    [articles],
  );

  return {
    articles,
    loading: isLoading,
    error: error as Error | null,
    getByCategory,
    refresh: refetch,
  };
}

export async function fetchArticle(slug: string | undefined): Promise<Article | null> {
  if (!slug) return null;
  const { data, error } = await supabase
    .from("articles")
    .select("*, categories(slug, name, color, image, icon)")
    .eq("slug", slug)
    .eq("status", "published")
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const cats = data.categories as {
    slug: string;
    name: string;
    color: string | null;
    image: string | null;
    icon: string | null;
  } | null;
  const catSlug = cats?.slug || DEFAULT_CATEGORY_SLUG;

  return {
    id: data.id,
    title: data.title,
    slug: data.slug,
    excerpt: data.excerpt || "",
    image: data.featured_image || FALLBACK_IMAGE,
    category: catSlug,
    categoryLabel: cats?.name || titleCaseSlug(catSlug),
    categoryColor: cats?.color ?? null,
    categoryImage: cats?.image ?? null,
    date: formatArticleDate(data.published_at || data.created_at),
    publishedAt: data.published_at || data.created_at,
    updatedAt: data.updated_at,
    readingTime: data.reading_time || DEFAULT_READING_TIME,
    content: data.content || undefined,
    authorName: data.author_name,
    tags: data.tags || [],
  } satisfies Article;
}

/** Fetch a single published article by slug — DB-only. */
export function useArticle(slug: string | undefined) {
  const {
    data: article = null,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["article", slug],
    queryFn: () => fetchArticle(slug),
    enabled: !!slug,
  });

  return {
    article,
    loading: isLoading,
    // Gate notFound on !isLoading: if the previous query errored and a new slug
    // begins loading, isError is still true — we must not show 404 while loading.
    notFound: !isLoading && (isError || !article),
  };
}
